import { useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { brandCreationSchema, type BrandCreationResult } from "../../shared/brandCreation";
import { api } from "../api";

export function AddBrandForm({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", homepageUrl: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [existing, setExisting] = useState<BrandCreationResult["brand"] | null>(null);
  const pending = useRef(false);
  const errorMessage = useRef<HTMLParagraphElement>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (pending.current) return;
    setError(""); setExisting(null);
    const parsed = brandCreationSchema.safeParse(form);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Check the brand name and website.");
      requestAnimationFrame(() => errorMessage.current?.focus());
      return;
    }
    pending.current = true; setSaving(true);
    try {
      const result = await api.createBrand(parsed.data);
      if (result.created) navigate(`/brands/${encodeURIComponent(result.brand.slug)}?tab=brand-kit`);
      else setExisting(result.brand);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not add this brand. Your entries are saved here; try again.");
      requestAnimationFrame(() => errorMessage.current?.focus());
    } finally { pending.current = false; setSaving(false); }
  };
  const update = (field: "name" | "homepageUrl", value: string) => { setForm((current) => ({ ...current, [field]: value })); setExisting(null); setError(""); };
  return <form id="add-brand-form" aria-label="Add brand" aria-describedby="add-brand-disclosure" aria-busy={saving} className="style-create brand-create" onSubmit={(event) => void submit(event)}>
    <header><div><h2>Add a brand</h2><p>Create a workspace directly from its website. No reference ad needed.</p></div></header>
    <div className="style-create__fields">
      <label><span>Brand name</span><input autoFocus required name="name" autoComplete="organization" maxLength={160} value={form.name} disabled={saving} onChange={(event) => update("name", event.target.value)} /></label>
      <label><span>Website URL</span><input required type="url" name="homepageUrl" autoComplete="url" placeholder="https://brand.com/" maxLength={2048} value={form.homepageUrl} disabled={saving} onChange={(event) => update("homepageUrl", event.target.value)} /></label>
    </div>
    <p id="add-brand-disclosure" className="brand-create__disclosure">Adding a new brand automatically starts website scanning and AI Brand Kit analysis using paid providers. Products may be discovered, but Creative Strategy scans are started separately by you from each Product Kit. No ad is generated.</p>
    {error && <p ref={errorMessage} tabIndex={-1} className="field-error" role="alert">{error}</p>}
    {existing && <div className="brand-create__existing" role="status"><p><strong>{existing.name}</strong> is already in your library. Nothing was changed and no new scan was requested.</p><Link className="secondary-button" to={`/brands/${encodeURIComponent(existing.slug)}?tab=brand-kit`}>Open existing brand</Link></div>}
    <footer><button type="button" className="secondary-button" disabled={saving} onClick={onClose}>Cancel</button><button type="submit" className="primary-button" disabled={saving || existing !== null}>{saving ? "Adding brand…" : "Add brand and scan website"}</button></footer>
  </form>;
}
